import React, { useState } from 'react';
import { KeyboardArrowUp } from '@mui/icons-material';
import {
  TableHead,
  TableRow as MTableRow,
  TableCell,
  TableBody,
  Table as MuiTable,
  Paper,
  Collapse,
  TableContainer,
} from '@mui/material';
import { type HeadCell } from './types';
import { IconButton } from '../IconButton';
import { Checkbox } from '../Checkbox';

export type TableRowProps<T> = {
  rowData: T;
  fontSize?: number;
  showIndex?: boolean;
  showCheckBox?: boolean;
  isFocused: boolean;
  isChecked?: boolean;
  isDraggable: boolean;
  defaultCollapsed?: boolean; // default behviour of collapsed row
  handleRowClick: () => void;

  index: number;
  visibleHeadCells: Array<HeadCell<T>>;
  collapsedHeadCells: Array<HeadCell<T>>;
  handleDragStart: (event: React.DragEvent<HTMLTableRowElement>) => void;
  handleDragOver: (event: React.DragEvent<HTMLTableRowElement>) => void;
  handleDrop: () => void;
  handleCheck?: () => void;
};

export const TableRow: React.FunctionComponent<TableRowProps<any>> = ({
  index,
  rowData,
  isFocused,
  isChecked = false,
  isDraggable,
  defaultCollapsed = true,
  handleRowClick,
  handleDragStart,
  handleDragOver,
  handleDrop,
  handleCheck = () => {},
  showIndex = false,
  showCheckBox = false,
  fontSize = 12,
  collapsedHeadCells,
  visibleHeadCells,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);
  const handleCollapseToggle = (): void => {
    setCollapsed(!collapsed);
  };
  const collapsedColumns = collapsedHeadCells.length;
  const isCollapsibleTable = collapsedColumns > 0;
  const visibleColumns = visibleHeadCells.length + Number(isCollapsibleTable);

  return (
    <TableContainer
      sx={{
        backgroundColor: isFocused ? '#F3E6E6' : '',
        width: '100%',
        '&:hover': {
          backgroundColor: isFocused ? '#F3E6E6' : '#f0f0f0',
        },
        overflowX: 'hidden',
        marginY: '2px',
        padding: 0,
      }}
      component={Paper}
      onClick={handleRowClick}
      draggable={isDraggable}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      key={index}
    >
      <MTableRow
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          overflowX: 'hidden',
        }}
      >
        {showIndex && (
          <TableCell
            width={'2%'}
            className="!p-0 !font-bold !text-[#951D1D] !whitespace-nowrap"
            sx={{
              border: 'none',
            }}
          >
            {index + 1}
          </TableCell>
        )}
        {showCheckBox && (
          <TableCell>
            <Checkbox
              size="small"
              value={isChecked}
              onChange={handleCheck}
              label=""
            />
          </TableCell>
        )}
        {visibleHeadCells.map(headCell => {
          const hoverText =
            typeof headCell.render(rowData) !== 'string'
              ? undefined
              : headCell.render(rowData)?.toString();

          const width: number = !headCell.minWidth
            ? 100 / visibleColumns
            : headCell.minWidth;

          return (
            <TableCell
              className="cursor-pointer"
              align="center"
              key={headCell.id as string}
              style={{
                width: `${width}%`,
                overflow: 'auto',
                maxHeight: '20em',
                fontSize,
                margin: 0,
                border: 'none',
              }}
              title={hoverText}
            >
              {headCell.render(rowData)}
            </TableCell>
          );
        })}

        {isCollapsibleTable && (
          <TableCell
            style={{
              width: `4%`,
              border: 'none',
            }}
          >
            <IconButton
              size="small"
              sx={{
                bgcolor: '#951D1D',
                color: 'white',
                ':hover': {
                  bgcolor: '#951D1D',
                  color: 'white',
                },
                transition: 'transform 0.3s ease',
                transform: !collapsed ? 'rotate(-180deg)' : 'rotate(-90deg)', // Rotating the icon when expanded
              }}
              onClick={handleCollapseToggle}
              icon={<KeyboardArrowUp />}
            />
          </TableCell>
        )}
      </MTableRow>
      {isCollapsibleTable && (
        <Collapse
          in={!collapsed}
          timeout="auto"
          unmountOnExit
          style={{
            marginLeft: '20px',
            marginRight: '20px',
          }}
        >
          <MuiTable size="small" aria-label="purchases">
            <TableHead>
              <MTableRow
                className={!collapsed ? 'expanded-row' : ''}
                sx={{
                  width: '100%',
                  bgcolor: '#B6B6B6 !important',
                  maxheight: '3.5em',
                  ...(!collapsed
                    ? {
                        borderBottom: 'none !important',
                      }
                    : {}),
                }}
              >
                {collapsedHeadCells.map(headCell => {
                  const width: number =
                    headCell.minWidth ?? 100 / collapsedColumns;
                  return (
                    <TableCell
                      align="center"
                      key={headCell.id as string}
                      style={{
                        width: `${width}%`,
                        fontSize,
                      }}
                    >
                      {headCell.label}
                    </TableCell>
                  );
                })}
              </MTableRow>
            </TableHead>
            <TableBody>
              <MTableRow
                sx={{
                  width: '100%',
                }}
              >
                {collapsedHeadCells.map(headCell => {
                  const hoverText =
                    typeof headCell.render(rowData) !== 'string'
                      ? undefined
                      : headCell.render(rowData)?.toString();

                  const width: number =
                    headCell.minWidth ?? 100 / collapsedColumns;

                  return (
                    <TableCell
                      className="cursor-pointer"
                      align="center"
                      key={headCell.id as string}
                      style={{
                        width: `${width}%`,
                        overflow: 'auto',
                        maxHeight: '18em',
                        fontSize,
                      }}
                      title={hoverText}
                    >
                      {headCell.render(rowData)}
                    </TableCell>
                  );
                })}
              </MTableRow>
            </TableBody>
          </MuiTable>
        </Collapse>
      )}
    </TableContainer>
  );
};

const MemoizedTableRow = React.memo(TableRow);
export { MemoizedTableRow };
